import DashboardLayout from "@/layouts/DashboardLayout";
import { useApi } from "@/hooks/useApi";

interface Invoice {
  id: string;
  number: string;
  description: string | null;
  amount: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
}

interface Subscription {
  id: string;
  plan: string;
  status: string;
  amount: number;
  billing_cycle: string;
  current_period_end: string | null;
}

export default function DashboardBilling() {
  const { data: invoices, loading: invLoading } = useApi<Invoice[]>("/invoices");
  const { data: subscription } = useApi<Subscription>("/dashboard/client");

  const statusColors: Record<string, string> = {
    paid: "text-cyber-green",
    sent: "text-cyber-orange",
    overdue: "text-cyber-red",
    draft: "text-silver-dim",
  };

  const sub = subscription as Subscription | null | undefined;



  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Billing & Invoices</h1>
          <p className="text-silver text-sm mt-1">Manage your subscription and view invoices.</p>
        </div>

        {/* Subscription Card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Current Plan</h2>
            <span className={`text-xs px-2.5 py-1 rounded-full border ${sub?.status === "active" ? "text-cyber-green border-cyber-green/30 bg-cyber-green/10" : "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10"}`}>
              {sub?.status || "No subscription"}
            </span>
          </div>
          {sub ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-silver-dim text-xs uppercase tracking-wider">Plan</p>
                <p className="text-white text-lg font-bold mt-0.5 capitalize">{sub.plan}</p>
              </div>
              <div>
                <p className="text-silver-dim text-xs uppercase tracking-wider">Amount</p>
                <p className="text-white text-lg font-bold mt-0.5">${sub.amount}/{sub.billing_cycle === "annual" ? "yr" : "mo"}</p>
              </div>
              <div>
                <p className="text-silver-dim text-xs uppercase tracking-wider">Renews</p>
                <p className="text-white text-lg font-bold mt-0.5">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "N/A"}</p>
              </div>
            </div>
          ) : (
            <p className="text-silver-dim text-sm">No active subscription.</p>
          )}
        </div>

        {/* Invoices */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
          <h2 className="text-white font-bold text-base mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Invoices</h2>
          {invLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              {invoices?.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors">
                  <div>
                    <p className="text-white text-sm font-medium">{inv.number}</p>
                    <p className="text-silver-dim text-xs mt-0.5">{inv.description || "Invoice"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-white text-sm font-bold">${inv.amount.toLocaleString()}</p>
                    <p className={`text-xs capitalize ${statusColors[inv.status]}`}>{inv.status}</p>
                  </div>
                </div>
              ))}
              {(!invoices || invoices.length === 0) && (
                <p className="text-silver-dim text-sm py-4 text-center">No invoices yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

